import classNames from 'classnames';
import { FunctionComponent, useMemo, useRef, useEffect } from 'react';

import { useBundle } from '@amzn/react-arb-tools';
import { useWeblab } from '@amzn/sustainability-central-shared-react';

import { ASX_REBRANDING } from 'src/constants';
import { useDropdownContext } from 'src/contexts/DropdownContext';
import { RESOURCE_BUNDLES } from 'src/enums/resource-bundles';
import { THEME } from 'src/enums/theme';
import { tf } from 'src/helpers';
import useDeviceSize from 'src/hooks/useDeviceSize';
import ChevronDownRebrandingIcon from 'src/icons/chevron-down-rebranding.svg';
import ChevronDownIcon from 'src/icons/chevron-down.svg';
import ChevronRightIcon from 'src/icons/chevron-right.svg';
import UserIcon from 'src/icons/user.svg';

import styles from './Account.module.scss';

export enum ACCOUNT_MENU_ACTIONS {
  SIGN_IN = 'signin',
  SIGN_OUT = 'signout',
  REGISTER = 'register',
  ACCOUNT = 'account',
}

interface AuthOptionProps {
  option: {
    stringId: string;
    label: string;
    action: ACCOUNT_MENU_ACTIONS;
  };
  onClick: () => void;
}

/**
 * Component for individual auth option row in dropdown.
 */
const AuthOption: FunctionComponent<AuthOptionProps> = ({ option, onClick }) => {
  const [bundle] = useBundle(RESOURCE_BUNDLES.HEADER);

  const renderedLabel = useMemo(() => {
    const text = tf(bundle, option.stringId, option.label);

    return text;
  }, [bundle, option.stringId, option.label]);

  return (
    <div
      className={styles.dropdownOption}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      role="option"
      aria-selected={false}
      tabIndex={0}
    >
      {renderedLabel}
    </div>
  );
};

type AccountVariant = 'primary' | 'secondary';
interface AccountProps {
  /** Variant describes the style of the account when the sub-nav or mobile-nav menus are expanded. "primary" variant for collapsed and "secondary" for expanded. */
  variant: AccountVariant;
  /** Theme describes the style of the account against the main theme of the navbar */
  theme?: THEME;
  /** Whether the user is authenticated */
  isAuthenticated?: boolean;
  ariaLabel?: string;
}

const UNAUTHENTICATED_OPTIONS = [
  { stringId: 'account_signIn', label: 'Sign in', action: ACCOUNT_MENU_ACTIONS.SIGN_IN },
  { stringId: 'account_register', label: 'New customer? Start here', action: ACCOUNT_MENU_ACTIONS.REGISTER },
];

const AUTHENTICATED_OPTIONS = [
  { stringId: 'account_myAccount', label: 'My Account', action: ACCOUNT_MENU_ACTIONS.ACCOUNT },
  { stringId: 'account_signOut', label: 'Sign out', action: ACCOUNT_MENU_ACTIONS.SIGN_OUT },
];

/**
 * Component for account button and dropdown.
 */
export const Account: FunctionComponent<AccountProps> = ({
  variant = 'primary',
  theme = THEME.LIGHT,
  isAuthenticated = false,
  ariaLabel,
}) => {
  const { isSmallDesktop } = useDeviceSize();
  const { isDropdownOpen, toggleDropdown, closeDropdown, registerDropdownRef } = useDropdownContext();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isExpanded = isDropdownOpen('account');

  const asxRebrandingWeblab = useWeblab(ASX_REBRANDING);
  const isRebrandingApplied =
    asxRebrandingWeblab === 'T1' || theme === THEME.REBRAND_DARK || theme === THEME.REBRAND_LIGHT;

  const DownIcon = isRebrandingApplied ? ChevronDownRebrandingIcon : ChevronDownIcon;

  // Register the dropdown ref for outside click detection
  useEffect(() => {
    registerDropdownRef('account', dropdownRef.current);
    return () => registerDropdownRef('account', null);
  }, [registerDropdownRef]);

  // Only apply the secondary (expanded) variant if the navbar is not dark themed. When dark themed, the collapsed and expanded variants are the same style.
  const isSecondary = variant === 'secondary' && theme !== THEME.DARK && theme !== THEME.REBRAND_DARK;

  /**
   * Handle auth option selection
   */
  const handleAuthAction = (action: string) => {
    if (action === ACCOUNT_MENU_ACTIONS.ACCOUNT) {
      window.location.assign('/account');
      closeDropdown();
      return;
    }

    if (action === ACCOUNT_MENU_ACTIONS.SIGN_OUT) {
      // The next call to useGetUserRights should set session item userRights = ['UNAUTHENTICATED']
      // Explicitly remove userRights to prevent unexpected behaviors
      sessionStorage.removeItem('userRights');
      window.location.assign('/sign-out');
      closeDropdown();
      return;
    }

    if (action === ACCOUNT_MENU_ACTIONS.SIGN_IN || action === ACCOUNT_MENU_ACTIONS.REGISTER) {
      window.location.assign(`/sign-in?redirectTo=${window.location.pathname}`);
    }

    closeDropdown();
  };

  // Get the appropriate options based on authentication status
  const authOptions = isAuthenticated ? AUTHENTICATED_OPTIONS : UNAUTHENTICATED_OPTIONS;
  const dropdown = (
    <div
      className={classNames(styles.dropdown, styles.show, styles[theme])}
      onMouseEnter={() => {
        // Keep dropdown open when mouse enters
      }}
      onMouseLeave={() => {
        closeDropdown();
      }}
    >
      {authOptions.map((option) => (
        <AuthOption key={option.action} option={option} onClick={() => handleAuthAction(option.action)} />
      ))}
    </div>
  );

  return (
    <div ref={dropdownRef}>
      <div
        role="menu"
        tabIndex={0}
        aria-label={ariaLabel}
        className={classNames(styles.account, styles[theme], {
          [styles.secondary]: isSecondary,
        })}
        onClick={() => {
          toggleDropdown('account');
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            toggleDropdown('account');
          }
        }}
      >
        <span className={styles.accountIcon}>
          <UserIcon className={styles.userIcon} />
        </span>

        {isSmallDesktop && (
          <span>
            <DownIcon className={classNames(styles.accountChevronIcon, { [styles.rotated]: isExpanded })} />
          </span>
        )}

        {/* Display auth options on mobile */}
        {!isSmallDesktop &&
          authOptions.map((option) => (
            <div className={styles.authOptionWrapper} key={option.action}>
              <AuthOption option={option} onClick={() => handleAuthAction(option.action)} />
              <ChevronRightIcon className={styles.accountChevronIcon} />
            </div>
          ))}
      </div>

      {/* Account Dropdown (auth options) on desktop */}
      {isSmallDesktop && isExpanded && dropdown}
    </div>
  );
};
